import React from "react";
import { HOME } from "@/constants/testIds";
import { FileText, Factory, ClipboardCheck, Package, Ship, Anchor, MapPin, Truck, Compass } from "lucide-react";

export const MilestoneVisibility = () => {
  const steps = [
    { icon: FileText,        label: "Purchase Order",   meta: "01 May",  status: "done" },
    { icon: Factory,         label: "Production",       meta: "10 May",  status: "done" },
    { icon: ClipboardCheck,  label: "Inspection",       meta: "20 May",  status: "done" },
    { icon: Package,         label: "Container Loaded", meta: "23 May",  status: "done" },
    { icon: Compass,         label: "Vessel Departure", meta: "26 May",  status: "done" },
    { icon: Ship,            label: "At Sea",           meta: "Now",     status: "current" },
    { icon: Anchor,          label: "Arrival",          meta: "12 Jun",  status: "todo" },
    { icon: MapPin,          label: "Customs",          meta: "13 Jun",  status: "todo" },
    { icon: Truck,           label: "Delivered",        meta: "15 Jun",  status: "todo" },
  ];

  return (
    <section
      id="milestones"
      data-testid={HOME.milestoneSection}
      className="relative py-24 md:py-32 bg-white border-y border-ink-10"
    >
      <div className="container-narrow grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] ink-soft mb-4">
            04 — Milestone Visibility
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold ink leading-[1.05] text-balance">
            From purchase order to delivered — every step timestamped.
          </h2>
          <p className="mt-5 text-[15px] ink-soft leading-relaxed max-w-md">
            Every shipment lives on a single vertical timeline. The current milestone
            is always one glance away. No spreadsheets. No emails.
          </p>

          <div className="mt-8 rounded-2xl glass p-4">
            <div className="flex items-center gap-2 text-[11px] font-mono ink-soft">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald dot-pulse" />
              CURRENT MILESTONE
            </div>
            <div className="mt-2 font-display text-2xl font-semibold ink">At Sea</div>
            <div className="mt-1 text-[13px] ink-soft">Mediterranean Sea · 14.2 kn · 168°</div>
            <div className="mt-3 text-[12px] font-mono ink">
              Next: <span className="text-emerald">Arrival · 12 Jun</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <ol className="relative">
            <span className="absolute left-[22px] top-2 bottom-2 w-px bg-ink-5" />
            {steps.map((s, i) => {
              const I = s.icon;
              const done = s.status === "done";
              const current = s.status === "current";
              return (
                <li
                  key={s.label}
                  data-testid={HOME.milestoneStep}
                  className="relative pl-16 py-3.5"
                >
                  {/* Connector colour overlay */}
                  {i > 0 && done && (
                    <span className="absolute left-[22px] top-0 h-[28px] w-px bg-emerald/50" />
                  )}
                  {/* Node */}
                  <span
                    className={`absolute left-[10px] top-3 grid h-7 w-7 place-items-center rounded-full border ${
                      done
                        ? "bg-emerald text-white border-[hsl(var(--emerald))]"
                        : current
                        ? "bg-white text-emerald border-[hsl(var(--emerald))] ring-emerald"
                        : "bg-ivory text-ink-soft border-ink-15"
                    }`}
                  >
                    <I className="h-3.5 w-3.5" />
                  </span>
                  {/* Card */}
                  <div
                    className={`flex items-center justify-between rounded-xl px-4 py-3 border ${
                      current
                        ? "bg-emerald/[0.04] border-[hsla(217,71%,35%,0.30)]"
                        : "bg-[hsl(var(--ivory))] border-ink-10"
                    }`}
                  >
                    <div>
                      <div className={`font-display text-[15px] font-semibold ${current ? "ink" : done ? "ink" : "ink-soft"}`}>
                        {s.label}
                      </div>
                      <div className="text-[11.5px] ink-soft font-mono mt-0.5">{s.meta}</div>
                    </div>
                    <span
                      className={`text-[10.5px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${
                        done
                          ? "bg-white text-emerald border-[hsla(217,71%,35%,0.25)]"
                          : current
                          ? "bg-emerald text-white border-[hsl(var(--emerald))]"
                          : "bg-white ink-soft border-ink-15"
                      }`}
                    >
                      {done ? "complete" : current ? "in progress" : "pending"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
